import { useEffect, useMemo } from "react";
import { Input, InputGroup, InputRightAddon } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { getFragmentData, gql } from "__generated__/gql";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import Form, {
  OnValidSubmitContext,
  OnValidSubmitHandler,
} from "components/Shared/Forms/Form";
import InlineFieldsWrapper from "components/Shared/Forms/InlineFieldsWrapper";
import {
  makePostgraphileErrorProcessor,
  getPostgraphileErrorMatchersForFields,
} from "utils/PostgraphileErrorExtensions";
import NumberInput from "components/Shared/Forms/NumberInput";
import TiHiFieldset from "components/Shared/Forms/TiHiFieldset";
import ProductLineSelect from "components/Shared/Select/ProductLineSelect";
import EnumSelect from "components/Shared/Select/EnumSelect";
import { SkuComponentType, WeightUnit } from "__generated__/gql/graphql";
import { WEIGHT_UNIT_LABELS } from "components/Shared/weightUnit";
import NamingOptions from "components/Products/NamingOptions";
import SkuComponents from "./SkuComponents";
import { getInitialSkuComponents } from "./SkuComponents/utils";
import ReadOnlyIdWrapper from "./ReadOnlyIdWrapper";
import {
  SkuPrimitiveInputs,
  skuFields,
  SkuInputs,
  skuSchema,
  ControlledFormField,
  FormField,
} from "./utils";

export const FORM_ID = "sku-form";

export type SubmitContext = OnValidSubmitContext<SkuInputs, object> & {
  disabledComponentTypes: SkuComponentType[];
};

const skuProductLineFieldsFragment = gql(/* GraphQL */ `
  fragment SelectedSkuProductLineFields on ProductLine {
    id
    name
    shortName
    nameSeparator
    isInternalSkuIdAppended
    innerCaseQuantity
  }
`);

const selectedProductLineQuery = gql(/* GraphQL */ `
  query SelectedSkuProductLine($productLineId: UUID!) {
    productLine(id: $productLineId) {
      id
      ...SelectedSkuProductLineFields
    }
  }
`);

export const processApolloError =
  makePostgraphileErrorProcessor<SkuPrimitiveInputs>(
    getPostgraphileErrorMatchersForFields(skuFields, {
      type: "Sku",
    }),
  );

type Props = {
  defaultValues?: Partial<SkuInputs>;
  disabledComponentTypes?: SkuComponentType[];
  idEditKey?: string;
  isCreateNew?: boolean;
  isInternalSkuIdPresent?: boolean;
  isLoading?: boolean;
  isUpcPresentByType?: Partial<Record<SkuComponentType, boolean>>;
  onValidSubmit: OnValidSubmitHandler<SkuInputs, object, SubmitContext>;
};

export default function SkuPanelForm({
  defaultValues: defaultValuesRaw,
  disabledComponentTypes: disabledComponentTypesProp,
  idEditKey,
  isCreateNew = false,
  isInternalSkuIdPresent = false,
  isLoading,
  isUpcPresentByType,
  onValidSubmit,
}: Props) {
  const defaultValues = useMemo(
    () => ({
      tiHi: null,
      skuComponents: getInitialSkuComponents(),
      ...defaultValuesRaw,
    }),
    [defaultValuesRaw],
  );

  const formContext = useForm<SkuInputs>({
    resolver: yupResolver(skuSchema),
    defaultValues,
  });

  const { reset } = formContext;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const productLineId = formContext.watch("productLineId");

  const { data: productLineData, loading: isLoadingProductLineData } =
    useBrandQuery(selectedProductLineQuery, {
      skip: (!isCreateNew && isLoading) || !productLineId,
      variables: productLineId ? { productLineId } : undefined,
      returnPartialData: true,
    });

  useEffect(() => {
    if (isCreateNew) {
      formContext.setFocus(
        defaultValues?.productLineId ? "name" : "productLineId",
      );
    }
  }, [formContext, isCreateNew, defaultValues]);

  const productLine = getFragmentData(
    skuProductLineFieldsFragment,
    productLineData?.productLine,
  );

  const disabledComponentTypes = useMemo(() => {
    const scts = disabledComponentTypesProp?.slice(0) ?? [];
    if (
      !productLine?.innerCaseQuantity &&
      !scts.includes(SkuComponentType.INNER)
    ) {
      scts.push(SkuComponentType.INNER);
    }
    return scts;
  }, [disabledComponentTypesProp, productLine]);

  const [name, shortName, internalSkuId] = formContext.watch([
    "name",
    "shortName",
    "internalSkuId",
  ]);

  return (
    <Form
      id={FORM_ID}
      {...{ formContext, onValidSubmit }}
      isLoaded={!isLoading || isCreateNew}
      getSubmitContext={(ctx) => ({ ...ctx, disabledComponentTypes })}
    >
      <ControlledFormField
        name="productLineId"
        id="ProductLineSelectedSku-product-line"
        renderInput={({ field }) => <ProductLineSelect {...field} />}
      />

      <FormField name="name" id="ProductLineSelectedSku-sku-name" />

      <NamingOptions
        internalSkuId={internalSkuId}
        isInternalSkuIdAppended={productLine?.isInternalSkuIdAppended}
        isLoading={isLoading || isLoadingProductLineData}
        nameSeparator={productLine?.nameSeparator}
        productLineName={productLine?.name}
        productLineShortName={productLine?.shortName}
        skuName={name}
        skuShortName={shortName}
      >
        <FormField name="shortName" placeholder={name || undefined} mt={0} />
      </NamingOptions>

      <FormField
        name="internalSkuId"
        id="ProductLineSelectedSku-internal-sku-id"
        renderInput={({ ref, ...rest }) => (
          <ReadOnlyIdWrapper
            key={idEditKey}
            isValuePresent={isInternalSkuIdPresent}
            renderInput={({ inputRef, isReadOnly }) => (
              <Input
                {...rest}
                ref={(c) => {
                  ref(c);
                  inputRef(c);
                }}
                isReadOnly={isReadOnly}
                fontFamily="mono"
                fontWeight={300}
              />
            )}
          />
        )}
      />

      <InlineFieldsWrapper>
        <ControlledFormField
          name="unitCogs"
          id="ProductLineSelectedSku-unit-cogs"
          renderInput={({ field }) => (
            <NumberInput
              {...field}
              max={100000}
              decimalScale={2}
              fixedDecimalScale
              prefix="$"
              allowNegative={false}
              minW="10rem"
            />
          )}
        />

        <ControlledFormField
          name="unitSrp"
          renderInput={({ field }) => (
            <NumberInput
              {...field}
              max={100000}
              decimalScale={2}
              fixedDecimalScale
              prefix="$"
              allowNegative={false}
              minW="10rem"
            />
          )}
        />
      </InlineFieldsWrapper>

      <InlineFieldsWrapper>
        <ControlledFormField
          name="unitNetWeight"
          id="ProductLineSelectedSku-unit-net-weight"
          renderInput={({ field }) => (
            <NumberInput
              {...field}
              max={999}
              decimalScale={4}
              allowNegative={false}
            />
          )}
        />

        <ControlledFormField
          name="unitNetWeightUnit"
          renderInput={({ field }) => (
            <EnumSelect
              {...field}
              enum={WeightUnit}
              labelsByValue={WEIGHT_UNIT_LABELS}
            />
          )}
        />
      </InlineFieldsWrapper>

      <SkuComponents
        {...{ formContext, idEditKey, isUpcPresentByType }}
        disabledSkuComponentTypes={disabledComponentTypes}
      />

      <ControlledFormField
        name="grossPalletWeightLbs"
        renderInput={({ field }) => (
          <InputGroup>
            <NumberInput
              {...field}
              max={9999}
              decimalScale={4}
              allowNegative={false}
              borderTopRightRadius={0}
              borderBottomRightRadius={0}
              minW="5.5em"
            />
            <InputRightAddon px={2}>lbs</InputRightAddon>
          </InputGroup>
        )}
      />

      <TiHiFieldset
        ControlledFormFieldComponent={ControlledFormField}
        fieldNamePrefix="tiHi"
        legendLabel="Ti/Hi"
      />
    </Form>
  );
}
